// 1 Construction の詳細。基本情報 InfoList + 施工箇所 + 工程実施 / 攪拌実施 / 作業実施 / 品質管理項目の 5 セクション。
// rev: mock(ConstructionExecutePage) のレイアウトに準拠し表示項目を整理。

import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Stack, Typography, Chip, Box, Button, Alert, Paper, Table, TableHead, TableBody, TableRow, TableCell
} from "@mui/material";
import EngineeringIcon from "@mui/icons-material/Engineering";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PageContext } from "../PageContext";
import { useFullDb } from "../../../api/dbSlice";
import { InfoList, InfoListColDef } from "../../../components/InfoList";
import { DataLink, MasterLink } from "../../../components/links/EntityLinks";
import { deriveAreaProcessCompletions } from "../../../types/derivedSelectors";
import { getAreaStatus, slotsToText, AREA_STATUS_COLOR, getAreaProcessViews } from "../../../util/areaStatus";
import { mixRatioDisplayName } from "../../../util/mixRatioLabel";
import { materialSearchUrl } from "../../../util/Materialperser";
import { getMaterialSetKg } from "../../../util/consumption";
import { useCreatorName } from "../../../util/useCreatorName";
import { useMountSync } from "../../../util/useMountSync";
import { ContentLoading } from "../../../components/Loading";
import { constructionCompletionState, constructionStatusLabel, deriveMixedAt } from "../../../util/constructionStatus";
import {
  formatDate,
  formatDateTime,
  formatHMS,
  workDetailName,
  areaLabel,
} from "./helpers";

// セクション内の「データなし」注記(このページの5セクション共通)。
function SectionEmptyText({ message }: { message: string }) {
  return <Typography variant="body2" color="text.secondary">{message}</Typography>;
}

export default function ConstructionDetailPage({ pageContext }: { pageContext: PageContext }) {
  const { id: cId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { db, loading, reload } = useFullDb(pageContext.api.indexedDB, pageContext.api.currentUser.id);

  // 詳細はローカルIDBを読むため、直接URL/リロードでも空にならないよう表示前にサーバ同期する。
  // 同期完了までローディング表示にする(同期前のローカル内容が一瞬表示されるのを防ぐ)。
  const syncing = useMountSync(pageContext.api.isOnline, () => pageContext.api.entities.syncConstructionData(), reload);

  const con = useMemo(() => db.constructions.find((c) => c.id === cId), [db.constructions, cId]);

  // 登録者名。created_by_user_id はサーバ設定列(キャッシュ同梱)で、名前はオンライン時に解決する。
  const creatorUserId = con?.created_by_user_id ?? null;
  const creatorName = useCreatorName(pageContext, creatorUserId);

  const pes = useMemo(
    () => db.processExecutions.filter((p) => p.construction_id === cId && p.deleted_at == null),
    [db.processExecutions, cId]
  );
  const stirs = useMemo(
    () => db.stirBatches.filter((s) => s.construction_id === cId && s.deleted_at == null).sort((a, b) => a.sort_order - b.sort_order),
    [db.stirBatches, cId]
  );
  const actions = useMemo(
    () => db.constructionActions.filter((a) => a.construction_id === cId && a.deleted_at == null).sort((a, b) => a.sort_order - b.sort_order),
    [db.constructionActions, cId]
  );
  const qualities = useMemo(
    () => db.qualityRecords.filter((q) => q.construction_id === cId && q.deleted_at == null),
    [db.qualityRecords, cId]
  );
  const consumptions = useMemo(
    () => db.constructionMaterialConsumptions.filter((m) => m.construction_id === cId && m.deleted_at == null),
    [db.constructionMaterialConsumptions, cId]
  );

  if (loading || syncing) {
    return (
      <Stack p={3} gap={3} maxWidth={1200} mx="auto">
        <Stack direction="row" alignItems="center" gap={1}>
          <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate("../")}>戻る</Button>
          <EngineeringIcon color="action" />
          <Typography variant="h4" fontWeight="bold">施工実施 詳細</Typography>
        </Stack>
        <ContentLoading />
      </Stack>
    );
  }
  if (!con) {
    return <Stack p={4}><Alert severity="error">施工実施 {cId} は存在しません</Alert></Stack>;
  }

  // この施工に紐づく施工箇所を PE 経由で解決。
  const areaIdSet = new Set(pes.map((p) => p.construction_plan_area_id));
  const linkedAreas = db.constructionPlanAreas.filter((a) => areaIdSet.has(a.id) && a.deleted_at == null);

  // 代表の配合比・材料: 最初の StirBatch → StirBatch.work_detail → 最初の PE.work_detail の優先順で導出。
  const firstStir = stirs[0];
  const firstStirWd = firstStir?.work_detail_id != null ? db.workDetails.find((w) => w.id === firstStir.work_detail_id) : undefined;
  const firstPeWd = pes.length > 0 ? db.workDetails.find((w) => w.id === pes[0]?.work_detail_id) : undefined;
  const representativeMixId = firstStir?.mix_ratio_definition_id ?? firstStirWd?.mix_ratio_definition_id ?? firstPeWd?.mix_ratio_definition_id ?? null;
  const representativeMix = representativeMixId != null ? db.mixRatioDefinitions.find((m) => m.id === representativeMixId) : undefined;
  const representativeMat = representativeMix?.material_id != null ? db.materials.find((m) => m.id === representativeMix.material_id) : undefined;

  // 工程実施数量計: constructionActions.actual_qty の合算。
  const totalActualQty = actions.reduce<number>((sum, a) => sum + (typeof a.actual_qty === "number" ? a.actual_qty : 0), 0);
  const subtypeIds = Array.from(new Set(linkedAreas.map((a) => a.work_subtype_id).filter((s): s is number => s != null)));
  const units = subtypeIds.map((sid) => db.workSubtypes.find((w) => w.id === sid)?.unit ?? "").filter((u) => u.length > 0);
  const uniqueUnits = Array.from(new Set(units));
  let qtyText: string;
  if (totalActualQty === 0) {
    qtyText = "-";
  } else {
    qtyText = `${totalActualQty}${uniqueUnits.length === 1 ? uniqueUnits[0] : ""}`;
  }

  const completionState = constructionCompletionState({ finished_at: con.finished_at, processExecutions: pes });
  const isCompleted = completionState === "completed";

  const infoCols: InfoListColDef[] = [
    { label: "ID", context: <DataLink type="construction" id={con.id} /> },
    { label: "材料", context: <MasterLink type="material" id={representativeMat?.id} name={representativeMat?.name ?? ""} /> },
    { label: "配合比", context: <MasterLink type="mix-ratio" id={representativeMix?.id} name={representativeMix ? mixRatioDisplayName(db, representativeMix) : ""} /> },
    { label: "紐付く工程実施数", context: pes.length },
    { label: "施工箇所数", context: linkedAreas.length },
    { label: "施工日", context: formatDate(con.executed_at) },
    { label: "攪拌開始", context: formatDateTime(deriveMixedAt(con.id, stirs)) },
    { label: "施工終了", context: formatDateTime(con.finished_at) },
    { label: "工程実施数量計 (記録のみ)", context: qtyText },
    { label: "気温", context: `${con.ambient_temperature ?? "-"}℃` },
    { label: "湿度", context: `${con.ambient_humidity ?? "-"}%` },
    { label: "消費QR数", context: consumptions.length },
    { label: "登録日", context: formatDateTime(con.createdAt) },
    { label: "登録者", context: creatorName },
    { label: "備考", context: con.remarks ?? "" },
  ];

  // 施工箇所テーブル: getAreaStatus / slotsToText で状態・場所を導出。
  const completions = deriveAreaProcessCompletions(db);
  const processDb = {
    workDetails: db.workDetails,
    mixRatioDefinitions: db.mixRatioDefinitions,
    materials: db.materials,
    plannedMaterials: db.constructionPlanAreaPlannedMaterials,
  };

  return (
    <Stack p={3} gap={3} maxWidth={1200} mx="auto">
      <Stack direction="row" alignItems="center" gap={1}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate("../")}>戻る</Button>
        <EngineeringIcon color="action" />
        <Typography variant="h4" fontWeight="bold">施工実施 詳細</Typography>
        <Chip
          size="small"
          color={isCompleted ? "success" : "warning"}
          icon={isCompleted ? <CheckCircleIcon /> : <ErrorIcon />}
          label={constructionStatusLabel(completionState)}
        />
        <Box flex={1} />
        <Button
          variant="contained"
          color={isCompleted ? "primary" : "warning"}
          startIcon={<EditIcon />}
          onClick={() => navigate("edit/")}
        >
          {isCompleted ? "編集" : "入力を再開"}
        </Button>
      </Stack>

      {/* 基本情報 */}
      <InfoList cols={infoCols} columns={3} />

      {/* 施工箇所 */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight="bold" mb={1}>施工箇所</Typography>
        {linkedAreas.length === 0 ? (
          <SectionEmptyText message="施工箇所はありません" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>施工箇所</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>工種 / 種別</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>使用材料</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">予定施工数量</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">工程進捗</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>場所</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>状態</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linkedAreas.map((a) => {
                const wt = db.workTypes.find((x) => x.id === a.work_type_id);
                const wst = db.workSubtypes.find((x) => x.id === a.work_subtype_id);
                const areaViews = getAreaProcessViews(a, completions, processDb);
                const completedCount = areaViews.filter((v) => v.isCompleted).length;
                const locText = slotsToText(a.id, db.constructionPlanAreaLocations, db.locationDivisions);
                const status = getAreaStatus(a, completions, db.workDetails);
                const statusColor = AREA_STATUS_COLOR[status];
                // 使用材料: 工程の配合比から重複を除いてリスト化。
                const matIdsSeen = new Set<number>();
                const areaMats: Array<{ id: number; name: string }> = [];
                for (const view of areaViews) {
                  if (view.materialId != null && !matIdsSeen.has(view.materialId)) {
                    matIdsSeen.add(view.materialId);
                    const mat = db.materials.find((m) => m.id === view.materialId);
                    if (mat) {
                      areaMats.push({ id: mat.id, name: mat.name });
                    }
                  }
                }
                return (
                  <TableRow key={a.id} hover>
                    <TableCell><DataLink type="plan-area" id={a.id} /></TableCell>
                    <TableCell>
                      <MasterLink type="work-type" id={wt?.id} name={wt?.name ?? ""} />
                      {" / "}
                      <MasterLink type="work-subtype" id={wst?.id} name={wst?.name ?? ""} />
                    </TableCell>
                    <TableCell>
                      {areaMats.length === 0 ? "-" : areaMats.map((m, idx) => (
                        <span key={m.id}>
                          {idx > 0 && " + "}
                          <MasterLink type="material" id={m.id} name={m.name} />
                        </span>
                      ))}
                    </TableCell>
                    <TableCell align="right">
                      {a.planned_qty ?? "-"}{wst?.unit ? ` ${wst.unit}` : ""}
                    </TableCell>
                    <TableCell align="right">{completedCount} / {areaViews.length}</TableCell>
                    <TableCell>{locText || "-"}</TableCell>
                    <TableCell><Chip size="small" color={statusColor} label={status} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* 工程実施 */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="baseline" gap={1} mb={1}>
          <Typography variant="h6" fontWeight="bold">工程実施</Typography>
          <Chip size="small" color="primary" label={`${pes.length} 件`} />
        </Stack>
        {pes.length === 0 ? (
          <SectionEmptyText message="工程実施はありません" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>施工箇所</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>工程</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>完了</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>材料</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pes.map((p) => {
                // 材料: 工程の配合比 → mixRatioDetails → materialDetails → materials。
                const wd = db.workDetails.find((w) => w.id === p.work_detail_id);
                const mixId = wd?.mix_ratio_definition_id ?? null;
                const matNames: string[] = [];
                if (mixId != null) {
                  const seenMat = new Set<number>();
                  for (const detail of db.mixRatioDetails) {
                    if (detail.deleted_at != null) {
                      continue;
                    }
                    if (detail.mix_ratio_definition_id !== mixId) {
                      continue;
                    }
                    const md = db.materialDetails.find((x) => x.id === detail.material_detail_id);
                    if (!md || md.material_id == null || seenMat.has(md.material_id)) {
                      continue;
                    }
                    seenMat.add(md.material_id);
                    const matEntry = db.materials.find((m) => m.id === md.material_id);
                    if (matEntry) {
                      matNames.push(matEntry.name);
                    }
                  }
                }
                return (
                  <TableRow key={p.id} hover>
                    <TableCell>{areaLabel(db, p.construction_plan_area_id)}</TableCell>
                    <TableCell>{workDetailName(db, p.work_detail_id)}</TableCell>
                    <TableCell>
                      {p.completed_at != null
                        ? <Chip size="small" color="success" icon={<CheckCircleIcon />} label="完了" />
                        : <Chip size="small" color="warning" variant="outlined" label="未完了" />}
                    </TableCell>
                    <TableCell>
                      {matNames.length === 0
                        ? <Typography variant="caption" color="text.secondary">-</Typography>
                        : (
                          <Stack direction="row" gap={0.5} flexWrap="wrap">
                            {matNames.map((name) => (
                              <Chip key={name} size="small" variant="outlined" label={name} />
                            ))}
                          </Stack>
                        )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* 攪拌実施 */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="baseline" gap={1} mb={1}>
          <Typography variant="h6" fontWeight="bold">攪拌実施</Typography>
          <Chip size="small" color="primary" label={`${stirs.length} 件`} />
        </Stack>
        {stirs.length === 0 ? (
          <SectionEmptyText message="攪拌実施はありません" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", width: 48 }}>#</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>配合比</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>主材料</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>施工箇所</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">攪拌時間</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">QR数</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">使用量</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stirs.map((s, i) => {
                const mix = s.mix_ratio_definition_id != null ? db.mixRatioDefinitions.find((m) => m.id === s.mix_ratio_definition_id) : undefined;
                const mainMat = mix?.material_id != null ? db.materials.find((m) => m.id === mix.material_id) : undefined;
                const linkedPe = s.process_execution_id != null ? db.processExecutions.find((p) => p.id === s.process_execution_id) : undefined;
                const stirAreaLabel = linkedPe ? areaLabel(db, linkedPe.construction_plan_area_id) : "-";
                // QR数: stir_batch_id でフィルタした consumptions。
                const stirConsumptions = consumptions.filter((c) => c.stir_batch_id === s.id);
                const stirQrIds = stirConsumptions.map((c) => c.material_qr_id);
                // 使用量kg = Σ used_ratio × 主材料のセットkg(受払簿と同式の getMaterialSetKg)。
                const usedRatioSum = stirConsumptions.reduce((sum, c) => sum + c.used_ratio, 0);
                const setKg = mainMat != null ? getMaterialSetKg(db.materialDetails, mainMat.id) : 0;
                const usedKg = setKg > 0 ? usedRatioSum * setKg : null;
                return (
                  <TableRow key={s.id} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{mix ? mixRatioDisplayName(db, mix) : "-"}</TableCell>
                    <TableCell>{mainMat?.name ?? "-"}</TableCell>
                    <TableCell>{stirAreaLabel}</TableCell>
                    <TableCell align="right">{formatHMS(s.duration_seconds)}</TableCell>
                    <TableCell align="right">
                      {stirQrIds.length > 0 ? (
                        <Link
                          to={materialSearchUrl(stirQrIds)}
                          style={{ color: "#1976d2", textDecoration: "underline", fontWeight: 600 }}
                        >
                          {stirQrIds.length}
                        </Link>
                      ) : (
                        stirQrIds.length
                      )}
                    </TableCell>
                    <TableCell align="right">{usedKg != null ? `${usedKg.toFixed(1)} kg` : "-"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* 作業実施 */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="baseline" gap={1} mb={1}>
          <Typography variant="h6" fontWeight="bold">作業実施</Typography>
          <Chip size="small" color="primary" label={`${actions.length} 件`} />
        </Stack>
        {actions.length === 0 ? (
          <SectionEmptyText message="作業実施はありません" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", width: 48 }}>#</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>工程</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>施工箇所</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">施工時間</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">実績数量</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {actions.map((a, i) => {
                // 施工箇所: process_execution_id → PE → area。
                // 複数 area は ConstructionActionArea でも保持するが、表示は PE 経由の代表値で十分。
                const actionPe = a.process_execution_id != null ? db.processExecutions.find((p) => p.id === a.process_execution_id) : undefined;
                const actionAreaLabel = actionPe ? areaLabel(db, actionPe.construction_plan_area_id) : "-";
                // 実績数量の単位は施工箇所の種別マスタから導出。
                const actionArea = actionPe ? db.constructionPlanAreas.find((x) => x.id === actionPe.construction_plan_area_id) : undefined;
                const actionUnit = actionArea?.work_subtype_id != null ? (db.workSubtypes.find((w) => w.id === actionArea.work_subtype_id)?.unit ?? "") : "";
                return (
                  <TableRow key={a.id} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{workDetailName(db, a.work_detail_id)}</TableCell>
                    <TableCell>{actionAreaLabel}</TableCell>
                    <TableCell align="right">{formatHMS(a.duration_seconds)}</TableCell>
                    <TableCell align="right">
                      {a.actual_qty != null ? `${a.actual_qty}${actionUnit ? ` ${actionUnit}` : ""}` : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* 品質管理項目 */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="baseline" gap={1} mb={1}>
          <Typography variant="h6" fontWeight="bold">品質管理項目</Typography>
          <Chip size="small" color="primary" label={`${qualities.length} 件`} />
        </Stack>
        {qualities.length === 0 ? (
          <SectionEmptyText message="品質管理項目はありません" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>施工箇所</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>測定項目</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>タイミング</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">測定値</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>合否</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {qualities.map((q) => {
                const qmi = db.qualityMeasurementItems.find((m) => m.id === q.quality_measurement_item_id);
                return (
                  <TableRow key={q.id} hover>
                    <TableCell>{areaLabel(db, q.construction_plan_area_id)}</TableCell>
                    <TableCell>{qmi?.name ?? "?"}</TableCell>
                    <TableCell>{qmi?.timing_category || "-"}</TableCell>
                    <TableCell align="right">
                      {q.value == null ? "未測定" : `${q.value}${qmi?.unit ? ` ${qmi.unit}` : ""}`}
                    </TableCell>
                    <TableCell>{renderQualityJudgmentChip(q.value, q.judgment)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}

// 合否 Chip: OK=success / NG=error / -(outlined) / null(value=null)=outlined "-"。
function renderQualityJudgmentChip(value: number | null, judgment: string | null) {
  if (value == null) {
    return <Chip size="small" variant="outlined" label="-" />;
  }
  if (judgment === "OK") {
    return <Chip size="small" color="success" label="OK" />;
  }
  if (judgment === "NG") {
    return <Chip size="small" color="error" label="NG" />;
  }
  return <Chip size="small" variant="outlined" label="-" />;
}
